// @flow

import type { UseFormReturn } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import type { FetchResponse } from '@gandi/resource/redux';

import { useNotifications } from 'src/hooks';
import type { FieldValues } from 'src/types/react-hook-form/field';

type ServerFieldError = {
  /** a message to the user */
  description: string,
  /** where the data has been passed to the server (ie: body) */
  location: string,
  /** name of the input field */
  name: string,
};

export type ServerErrorResponse = {
  response: {
    data: {
      /** http status code */
      code: number,
      /** http status message */
      cause: string,
      /** array of field errors */
      errors: Array<ServerFieldError>,
    },
  },
};

export type Response =
  | FetchResponse<
      201,
      { message: 'Resource Created', id: string, href: string },
    >
  | FetchResponse<202, { message: 'Request Accepted' }>;

export const useHandleFormErrorSubmit: <T: FieldValues>(
  form: UseFormReturn<T>,
) => ServerErrorResponse => void = form => {
  const { notifyMaybeApiError } = useNotifications();
  const dispatch = useDispatch();

  return ({ response }: ServerErrorResponse) => {
    // set form in error state
    form.setError('root.serverError', {
      type: response?.data?.code || 'network',
    });

    // handle api form errors first
    if (response?.data?.errors) {
      response.data.errors.forEach((errObj: ServerFieldError, i) => {
        form.setError(
          errObj.name,
          {
            // type is needed by the setError function
            // it is made to
            // https://react-hook-form.com/docs/useform/seterror
            type: 'api',
            message: errObj.description,
          },
          { shouldFocus: i === 0 },
        );
      });
    } else {
      dispatch(notifyMaybeApiError(response));
    }
  };
};
